import { formatMoney } from '../money';
import type { ShippingRule } from '../settings';
import { formatKm } from './correios';
import { UF_INFO, cepInRange, normalizeCity, toUF, type GeoPrecision } from './geo';

/**
 * Regras de frete por região (desconto geral, por estado, cidade, faixa de CEP ou raio da loja).
 *
 * Avaliação, para CADA opção de entrega/envio separadamente (retirada nunca recebe regra):
 * 1. As regras são lidas na ordem da lista; vale a PRIMEIRA que casar (uma regra por opção).
 * 2. Casa quando: está ativa; hoje (fuso da loja) está entre `startsOn` e `endsOn` (inclusive); `appliesTo`
 *    combina com a opção (all, local ou shipping); o subtotal dos produtos (preço promocional, antes de cupom
 *    e frete) atinge `minOrderCents`; e o destino está no escopo (all, states, cities sem diferenciar
 *    maiúsculas e acentos, cep_ranges inclusive, radius em km de rua estimados a partir da loja).
 * 3. Efeito: free (frete zero), percent (value % de desconto), fixed (desconto de value centavos) ou
 *    fixed_price (frete sai por value centavos, nunca mais caro que o calculado). Nunca fica negativo.
 * 4. Opção "a combinar" (sem valor): só regras free e fixed_price se aplicam (viram valor definido).
 * 5. Raio exige localização da rua (com localização só da cidade, a regra de raio não casa).
 */

export interface RuleDestination {
  cep: string | null;
  city: string | null;
  uf: string | null;
  precision: GeoPrecision;
}

export interface RuleContext {
  destination: RuleDestination;
  /** Distância de rua estimada da loja até o destino (null quando desconhecida). */
  roadKmFromStore: number | null;
  /** Subtotal dos produtos (com preço promocional), antes de cupom e frete. */
  subtotalCents: number;
  /** Hoje no fuso da loja, AAAA-MM-DD. */
  today: string;
}

export interface RuleOutcome {
  rule: ShippingRule;
  badge: string;
  discountCents: number;
  /** Valor final da opção depois da regra. */
  finalCents: number;
}

export function isRuleActiveOn(rule: Pick<ShippingRule, 'active' | 'startsOn' | 'endsOn'>, today: string): boolean {
  if (!rule.active) return false;
  if (rule.startsOn && today < rule.startsOn) return false;
  if (rule.endsOn && today > rule.endsOn) return false;
  return true;
}

export function ruleAppliesToKind(rule: Pick<ShippingRule, 'appliesTo'>, kind: 'local' | 'shipping'): boolean {
  return rule.appliesTo === 'all' || rule.appliesTo === kind;
}

export function ruleMatchesDestination(rule: ShippingRule, ctx: Pick<RuleContext, 'destination' | 'roadKmFromStore'>): boolean {
  const d = ctx.destination;
  switch (rule.scope) {
    case 'all':
      return true;
    case 'states': {
      const uf = toUF(d.uf);
      return Boolean(uf && rule.states.includes(uf));
    }
    case 'cities': {
      const uf = toUF(d.uf);
      const city = normalizeCity(d.city);
      if (!uf || !city) return false;
      return rule.cities.some((c) => c.state === uf && normalizeCity(c.city) === city);
    }
    case 'cep_ranges':
      return Boolean(d.cep) && rule.cepRanges.some((r) => cepInRange(d.cep, r.from, r.to));
    case 'radius':
      return (
        rule.radiusKm != null &&
        d.precision === 'street' &&
        ctx.roadKmFromStore != null &&
        ctx.roadKmFromStore <= rule.radiusKm
      );
    default:
      return false;
  }
}

/** Desconto da regra sobre um valor (null = a combinar). Retorna null quando o efeito não se aplica. */
export function ruleEffect(rule: Pick<ShippingRule, 'effect' | 'value'>, priceCents: number | null): { finalCents: number; discountCents: number } | null {
  if (priceCents == null) {
    if (rule.effect === 'free') return { finalCents: 0, discountCents: 0 };
    if (rule.effect === 'fixed_price') return { finalCents: Math.max(0, rule.value), discountCents: 0 };
    return null;
  }
  const price = Math.max(0, priceCents);
  let final = price;
  if (rule.effect === 'free') final = 0;
  else if (rule.effect === 'percent') final = price - Math.round((price * Math.min(100, Math.max(0, rule.value))) / 100);
  else if (rule.effect === 'fixed') final = price - Math.max(0, rule.value);
  else if (rule.effect === 'fixed_price') final = Math.min(price, Math.max(0, rule.value));
  final = Math.max(0, final);
  return { finalCents: final, discountCents: price - final };
}

/**
 * Primeira regra que casa com a opção. `priceCents` null = a combinar.
 * Opção com valor zero não recebe regra (não há o que descontar).
 */
export function applyShippingRules(
  rules: readonly ShippingRule[],
  ctx: RuleContext,
  option: { kind: 'local' | 'shipping'; priceCents: number | null },
): RuleOutcome | null {
  if (option.priceCents === 0) return null;
  for (const rule of rules) {
    if (!isRuleActiveOn(rule, ctx.today)) continue;
    if (!ruleAppliesToKind(rule, option.kind)) continue;
    if (rule.minOrderCents != null && ctx.subtotalCents < rule.minOrderCents) continue;
    if (!ruleMatchesDestination(rule, ctx)) continue;
    const eff = ruleEffect(rule, option.priceCents);
    if (!eff) continue;
    return { rule, badge: ruleBadge(rule), discountCents: eff.discountCents, finalCents: eff.finalCents };
  }
  return null;
}

// ---------------------------------------------------------------- Texto do selo

/** "R$ 199" quando não tem centavos; senão "R$ 9,90". */
export function moneyShort(cents: number): string {
  return formatMoney(cents).replace(/,00$/, '');
}

function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} e ${names[names.length - 1]}`;
}

function scopePhrase(rule: ShippingRule, forFixedPrice: boolean): string {
  switch (rule.scope) {
    case 'states': {
      const names = rule.states.map((uf) => UF_INFO[uf as keyof typeof UF_INFO]?.name ?? uf);
      if (!names.length) return '';
      if (names.length <= 2) return `para ${joinNames(names)}`;
      if (names.length === 3) return `para ${joinNames([...rule.states])}`;
      return `para ${rule.states.slice(0, 3).join(', ')} e outros estados`;
    }
    case 'cities': {
      const names = rule.cities.map((c) => c.city.trim());
      if (!names.length) return '';
      return names.length <= 2 ? `em ${joinNames(names)}` : `em ${names[0]} e mais ${names.length - 1} cidades`;
    }
    case 'cep_ranges':
      return 'em regiões selecionadas';
    case 'radius':
      return rule.radiusKm != null ? `até ${formatKm(rule.radiusKm)}${forFixedPrice ? '' : ' da loja'}` : '';
    default:
      return '';
  }
}

/** Texto curto para o catálogo. Usa o da regra ou gera (ex.: "Frete grátis em Goiânia acima de R$ 199"). */
export function ruleBadge(rule: ShippingRule): string {
  if (rule.badge.trim()) return rule.badge.trim();
  const subject = rule.appliesTo === 'local' ? 'Entrega' : 'Frete';
  const min = rule.minOrderCents != null && rule.minOrderCents > 0 ? `acima de ${moneyShort(rule.minOrderCents)}` : '';
  let text: string;
  if (rule.effect === 'fixed_price') {
    text = [subject, scopePhrase(rule, true), `por ${moneyShort(rule.value)}`, min].filter(Boolean).join(' ');
  } else {
    const effect =
      rule.effect === 'free'
        ? `${subject} grátis`
        : rule.effect === 'percent'
          ? `${Math.min(100, rule.value)}% de desconto ${rule.appliesTo === 'local' ? 'na entrega' : 'no frete'}`
          : `${moneyShort(rule.value)} de desconto ${rule.appliesTo === 'local' ? 'na entrega' : 'no frete'}`;
    text = [effect, scopePhrase(rule, false), min].filter(Boolean).join(' ');
  }
  return text.length > 80 ? `${text.slice(0, 79).trimEnd()}…` : text;
}

/** Selos das regras ativas hoje, na ordem da lista (para o catálogo anunciar). */
export function activeRuleBadges(
  rules: readonly ShippingRule[],
  today: string,
  limit = 3,
): { ruleId: string; text: string; appliesTo: ShippingRule['appliesTo']; minOrderCents: number | null; free: boolean }[] {
  return rules
    .filter((r) => isRuleActiveOn(r, today))
    .slice(0, limit)
    .map((r) => ({ ruleId: r.id, text: ruleBadge(r), appliesTo: r.appliesTo, minOrderCents: r.minOrderCents, free: r.effect === 'free' }));
}
